import { Input } from "@/app/components/ui/input";
import { Flex, Text } from "@radix-ui/themes";
import React, { useState } from "react";
import HighestBidToolTip from "../HighestBidToolTip";
import { Button } from "@/app/components/ui/button";
import { AuctionState, BidType, BiddingSchedule, BiddingScheduleRange } from "@prisma/client";
import { formatCurrency } from "@/lib/utils";
import MaxBidDisabledTooltip from "../MaxBidDisabledTooltip";
import BidScheduleTable from "../accordion/BidScheduleTable";
import { HybridTooltip, HybridTooltipTrigger, HybridTooltipContent, TouchProvider } from "@/app/components/ui/hybrid-tooltip";
import { Loader2 } from "lucide-react";

interface Props {
  auctionState: AuctionState;
  placeBid: ({
    amount,
    bidType,
  }: {
    amount: number;
    bidType: BidType;
  }) => Promise<void>;
  minBid: number;
  isSubmitting: boolean;
  isCurrentUserWinner: boolean;
  error: string;
  expired: boolean;
  disableBid: boolean;
  disableMaxBid?: boolean;
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
}

const BiddingCardInput = ({
  auctionState,
  placeBid,
  minBid,
  error,
  isSubmitting,
  isCurrentUserWinner,
  expired,
  disableBid,
  disableMaxBid = false,
  biddingSchedule,
}: Props) => {
  const [userBid, setUserBid] = useState("");

  // Sanitize user input and parse as number. Allows decimals; parent enforces whole dollars.
  const sanitizeAndParse = (raw: string): number => {
    const sanitized = (raw ?? "").replace(/[^\d.]/g, "");
    if (sanitized === "") return NaN;
    return parseFloat(sanitized);
  };

  const handlePlaceBidClick = () => {
    if (expired && process.env.NODE_ENV === "production") {
      alert("This lot is closed!");
      return;
    }
    const amount = sanitizeAndParse(userBid);
    placeBid({ amount, bidType: BidType.STANDARD });
    setUserBid("");
  };

  const handleSetMaxBidClick = () => {
    if (expired && process.env.NODE_ENV === "production") {
      alert("This lot is closed!");
      return;
    }
    if (disableBid && process.env.NODE_ENV === "production") {
      alert("The auction is not active!");
      return;
    }
    if (
      auctionState === AuctionState.PREVIEW &&
      process.env.NODE_ENV === "production"
    ) {
      alert("The auction has not yet started!");
      return;
    }
    const amount = sanitizeAndParse(userBid);
    placeBid({ amount, bidType: BidType.MAX });
    setUserBid("");
  };

  return (
    <>
      <Flex direction="column" className="mb-1 w-full" gap="3">
        <Input
          className="bg-gray-50 h-10 mb-1"
          value={userBid}
          onChange={(e) => setUserBid(e.target.value)}
          disabled={isSubmitting || expired || disableBid}
          placeholder={
            auctionState === AuctionState.PREVIEW
              ? "The auction has not yet started."
              : expired
                ? `This lot has closed.`
                : disableBid
                  ? "The auction is not active."
                  : `${formatCurrency(minBid)} or up`
          }
        />
        <BidErrorDisplay error={error} biddingSchedule={biddingSchedule} currentAmount={minBid} />
        <Flex width="100%" className="space-x-3">
          {isCurrentUserWinner ? (
            <div className="flex-1">
              <HighestBidToolTip>
                <Button
                  variant="outline"
                  disabled={true}
                  className="w-full h-10"
                >
                  Place bid
                </Button>
              </HighestBidToolTip>
            </div>
          ) : (
            <div className="flex-1">
              <Button
                variant="outline"
                className="flex-1 h-10 border-magenta w-full"
                disabled={
                  isSubmitting ||
                  expired ||
                  disableBid
                }
                onClick={handlePlaceBidClick}
              >
                {isSubmitting && <Loader2 className="w-4 h-4" />}
                {isSubmitting ? 'Placing bid...' : 'Place bid'}
              </Button>
            </div>
          )}
          {disableMaxBid ? ( null ) : (
            <div className="flex-1">
              <Button
                className="flex-1 h-10 w-full bg-magenta hover:bg-dark-magenta disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
                disabled={isSubmitting || expired || disableBid}
                onClick={handleSetMaxBidClick}
              >
                Set max bid
              </Button>
            </div>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default BiddingCardInput;

// Extracted component for rendering bid input errors
const INCREMENT_SCHEDULE_ERROR = "Please enter a bid that respects the bid increment schedule.";

const BidErrorDisplay = ({
  error,
  biddingSchedule,
  currentAmount,
}: {
  error: string;
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
  currentAmount?: number;
}) => {
  if (!error) return null;

  const isIncrementScheduleError = error === INCREMENT_SCHEDULE_ERROR;

  if (!isIncrementScheduleError) {
    return (
      <Flex className="w-full" justify="end">
        <Text className="justify-end text-red-500 text-sm">{error}</Text>
      </Flex>
    );
  }

  return (
    <Flex className="w-full" justify="end">
      <TouchProvider>
        <HybridTooltip delayDuration={150}>
          <HybridTooltipTrigger asChild>
            <span className="block w-full text-left text-red-500 text-sm underline underline-offset-4 decoration-dotted cursor-pointer">
              {error}
            </span>
          </HybridTooltipTrigger>
          <HybridTooltipContent side="top" align="end" popoverClassName="p-0 bg-transparent shadow-none text-gray-900" tooltipClassName="p-0">
            <MiniBidSchedule biddingSchedule={biddingSchedule} currentAmount={currentAmount} />
          </HybridTooltipContent>
        </HybridTooltip>
      </TouchProvider>
    </Flex>
  );
};

// Compact schedule for popover/tooltip
const MiniBidSchedule = ({
  biddingSchedule,
  currentAmount,
}: {
  biddingSchedule: BiddingSchedule & { BiddingScheduleRange: BiddingScheduleRange[] };
  currentAmount?: number;
}) => {
  const ranges = biddingSchedule.BiddingScheduleRange;

  // Build display rows with calculated starts
  const rows = [] as { start: number; end: number; increment: number }[];
  let start = 0;
  for (let i = 0; i < ranges.length; i++) {
    const end = ranges[i].rangeEnd;
    rows.push({ start, end, increment: ranges[i].increment });
    start = end + ranges[i].increment; // ensure next range starts at next valid bid
  }

  const format = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isInRow = (row: { start: number; end: number }, isLast: boolean) => {
    if (currentAmount == null) return false;
    if (isLast) return currentAmount >= row.start;
    return currentAmount >= row.start && currentAmount <= row.end;
  };

  return (
    <div className="min-w-[280px] max-w-[90vw] rounded-md border bg-white shadow-md text-gray-900">
      <div className="px-3 py-2 border-b">
        <div className="text-xs font-semibold text-gray-900">Bid increments</div>
        <div className="text-[10px] text-gray-600">All amounts in CAD</div>
      </div>
      <div className="grid grid-cols-2 gap-x-2 text-[11px] px-3 py-2 text-gray-900">
        <div className="font-medium">Bid amount</div>
        <div className="font-medium text-right">Min increment</div>
      </div>
      <div className="max-h-64 overflow-auto px-1 pb-2 pt-1">
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1;
          const highlight = isInRow(row, isLast);
          return (
            <div
              key={idx}
              className={
                "grid grid-cols-2 gap-x-2 items-center rounded-sm px-2 py-1 mx-1" +
                (highlight ? " bg-amber-50 ring-1 ring-amber-300" : " hover:bg-slate-50")
              }
            >
              <div>
                ${""}{`${isLast ? `${format(row.start)}+` : `${format(row.start)} - ${format(row.end)}`}`}
              </div>
              <div className="text-right font-medium">${""}{`${format(row.increment)}`}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
